import ObjectUtils from '@spothero/utils/object';
import {AmenityTypes} from 'api/search-v2/utils';
import SpotUtils from './spot';
import dayjs from 'dayjs';
import {creditCardTransformer} from './user-utils';

export const OVERSIZE_FEE_TYPE = 'oversize_fee';
import {RESERVATION_TYPES} from './types/reservation';

const ReservationUtils = {
    ...RESERVATION_TYPES,

    getMonthlyInstructionsFromParkingPassType(parkingPassType) {
        switch (parkingPassType) {
            case this.PARKING_PASS_TYPES_ENUM.SCAN_IN_OUT:
                return 'Scan your pass to enter and exit the facility.';
            case this.PARKING_PASS_TYPES_ENUM.LICENSE_PLATE:
                return 'Your license plate number will allow you to park at this facility.';
            case this.PARKING_PASS_TYPES_ENUM.SCAN_IN_TICKET_OUT:
                return 'Scan your pass to receive a prevalidated ticket and enter the facility. Insert your ticket to leave.';
            case this.PARKING_PASS_TYPES_ENUM.PULL_TICKET_SCAN_OUT:
                return 'Pull a ticket to enter the facility. When you leave insert your ticket then scan your pass.';
            case this.PARKING_PASS_TYPES_ENUM.PULL_TICKET_CALL_BUTTON:
                return 'Pull a ticket to enter the facility. When you leave press the call button at the exit gate.';
            case this.PARKING_PASS_TYPES_ENUM.SELF_PARK_SEE_ATTENDANT:
                return 'When you arrive see the attendant to check in.';
            case this.PARKING_PASS_TYPES_ENUM.PULL_TICKET_SEE_ATTENDANT:
                return 'Pull a ticket when you arrive then see the attendant to check in.';
            case this.PARKING_PASS_TYPES_ENUM.VALET:
                return 'See attendant when you arrive and they will park your car.';
            case this.PARKING_PASS_TYPES_ENUM.VENDGATE:
                return 'Use the VendGate link in your parking pass to open the gate.';
            case this.PARKING_PASS_TYPES_ENUM.PRINTOUT:
                return 'You will display your printed parking pass at this facility.';
            case this.PARKING_PASS_TYPES_ENUM.OPERATOR_SCAN:
                return 'The attendant will scan your parking pass at this facility.';
            case this.PARKING_PASS_TYPES_ENUM.HANG:
                return 'You will receive a hang tag to park at this facility.';
            case this.PARKING_PASS_TYPES_ENUM.ACCESS_CARD:
                return 'You will receive an access pass to park at this facility.';
            case this.PARKING_PASS_TYPES_ENUM.STICKER:
                return 'You will receive a sticker to park at this facility.';
            default:
                return 'You will display your parking pass at this facility.';
        }
    },

    PARKING_PASS_REDEMPTION_TYPE: {
        SCAN_CODE: 'scanCode',
        LICENSE_PLATE: 'licensePlate',
        DISPLAY_ID: 'displayId',
    },

    DONT_KNOW_LICENSE_VALUE: 'DONTKNOW',

    NEW_VEHICLE_ERRORS: {
        NO_VEHICLE:
            "Please select a vehicle. If your vehicle is not listed choose 'Vehicle Not Listed'.",
    },

    ACTIVE_STATUS: {
        NOT_STARTED: "Reservation Hasn't Started Yet",
        ACTIVE: 'Reservation Active',
        ENDED: 'Reservation Has Ended',
    },

    ACTIVE_TAB_VALUES: {
        PASS: 'pass',
        DETAILS: 'details',
        HOW_TO: 'howTo',
    },

    // This string is very tightly coupled to the `facilities.py` serializer. they should be changed in tandem and/or refactored
    IN_OUT_RESTRICTION:
        'This facility does NOT allow in/out privileges. You CANNOT enter & exit more than once.',

    // array of all parking pass types that likely use license plate for redemption
    get LICENSE_PLATE_REDEMPTION_TYPES() {
        return [
            this.PARKING_PASS_TYPES_ENUM.PRINTOUT,
            this.PARKING_PASS_TYPES_ENUM.LICENSE_PLATE,
            this.PARKING_PASS_TYPES_ENUM.LPR,
            this.PARKING_PASS_TYPES_ENUM.GATED_LPR_WITH_SCAN,
        ];
    },

    ERROR_MESSAGES: {
        NOT_FOUND: 'Not Found',
        NO_PERMISSION: 'You do not have permission to perform this action.',
        NO_CREDS: 'Authentication credentials were not provided.',
        FORBIDDEN: 'Forbidden',
    },

    getErrorTypeFromServerError(serverError) {
        const errorMessage = serverError?.message || serverError?.statusText;
        const errorStatus = serverError?.status;

        if (!errorMessage) {
            return this.POST_PURCHASE_ERRORS_ENUM.GENERIC_ERROR;
        }

        // This is kind of a mess because depending on which api route & environment the errors can be pretty different shapes
        if (
            [
                this.ERROR_MESSAGES.NO_PERMISSION,
                this.ERROR_MESSAGES.NO_CREDS,
                this.ERROR_MESSAGES.FORBIDDEN,
            ].includes(errorMessage) ||
            errorMessage.includes(403) ||
            errorStatus === 403
        ) {
            return this.POST_PURCHASE_ERRORS_ENUM.ACCESS_DENIED;
        } else if (
            [this.ERROR_MESSAGES.NOT_FOUND].includes(errorMessage) ||
            errorMessage.includes('404') ||
            errorStatus === 404
        ) {
            return this.POST_PURCHASE_ERRORS_ENUM.NOT_FOUND;
        }

        return this.POST_PURCHASE_ERRORS_ENUM.GENERIC_ERROR;
    },

    buildMonthlyRestrictions(camelizedRes) {
        const {
            monthlyInOutDetail,
            parkingPassType,
            monthlyDescription,
            monthlyContractDetail,
            currencyType,
            currencySymbol,
            facility: {heightRestrictionDescription},
        } = camelizedRes;

        const currency = `${
            currencyType === 'cad' ? 'CA' : ''
        }${currencySymbol}`;

        const monthlyRestrictions = [
            this.getMonthlyInstructionsFromParkingPassType(parkingPassType),
        ];

        if (heightRestrictionDescription?.length) {
            monthlyRestrictions.unshift(heightRestrictionDescription);
        }

        if (monthlyDescription?.length) {
            monthlyRestrictions.push(monthlyDescription);
        }

        if (
            monthlyInOutDetail &&
            SpotUtils.IN_OUT_MESSAGES[monthlyInOutDetail.inOutAllowed]
        ) {
            const inOutRestriction = SpotUtils.IN_OUT_MESSAGES[
                monthlyInOutDetail.inOutAllowed
            ]({
                currency,
                fee: monthlyInOutDetail.inOutFee,
                limit: monthlyInOutDetail.inOutLimit,
                limit_type: monthlyInOutDetail.inOutLimitType, // eslint-disable-line camelcase
            });

            monthlyRestrictions.push(inOutRestriction);
        }

        if (monthlyContractDetail && monthlyContractDetail.contractRequired) {
            const feeMessage = monthlyContractDetail.contractTerminationFee
                ? `There is a ${currency}${monthlyContractDetail.contractTerminationFee} fee for cancellation.`
                : 'There is no fee for cancellation.';
            const cancellationMessage = monthlyContractDetail.cancellationRequired
                ? ` If you cancel your contract, a ${monthlyContractDetail.cancellationPeriod}-day notice is required.`
                : '';
            const contractDetail = `This location requires a ${monthlyContractDetail.contractPeriod} month commitment. <b>${feeMessage}</b>${cancellationMessage}`;

            monthlyRestrictions.push(contractDetail);
        }

        if (camelizedRes.ruleReservationType === '247') {
            const noStorageMessage =
                'No Vehicle Storage. No vehicles may be stored in this parking facility for more than 5 consecutive business days without exiting the facility. If you&apos;re planning on leaving your vehicle for an extended period of time, please contact the facility manager for approval. Any vehicle stored in this facility over 5 days without notification is subject to towing at the vehicle owner&apos;s expense.';

            monthlyRestrictions.push(noStorageMessage);
        }

        return monthlyRestrictions;
    },

    transformResponse(response) {
        if (!response) {
            return {};
        }

        const camelizedRes = ObjectUtils.camelizeKeys(response);
        const isMonthly =
            camelizedRes.reservationType === this.RESERVATION_TYPES.MONTHLY;
        const hasInOutAmenity = Boolean(
            camelizedRes.amenitiesFull.find(
                amenity => amenity.slug === AmenityTypes.IN_OUT
            )
        );
        let restrictions = [...camelizedRes.facility.restrictions];

        if (camelizedRes.postPurchaseInstructions?.length) {
            restrictions.unshift(camelizedRes.postPurchaseInstructions);
        }

        if (hasInOutAmenity) {
            restrictions = restrictions.filter(
                restriction => restriction !== this.IN_OUT_RESTRICTION
            );
        }

        if (isMonthly) {
            restrictions = [...this.buildMonthlyRestrictions(camelizedRes)];
        }

        const accessHours = SpotUtils.formatAccessHours(
            response.access_hours // This field is either facility hours or overridden (on BE) as monthly hours
        );

        return {
            canRefundAsSpotheroCredit: camelizedRes.canRefundAsSpotheroCredit,
            cancellationAllowed: camelizedRes.cancellationAllowed,
            cancellationMinutes: camelizedRes.cancellationMinutes,
            timezone: camelizedRes.timezone,
            starts: camelizedRes.starts,
            ends: camelizedRes.ends,
            entryTimestamp: camelizedRes.entryTimestamp,
            exitTimestamp: camelizedRes.exitTimestamp,
            vehicleRequired:
                camelizedRes.facility.oversizeFee > 0 ||
                camelizedRes.facility.licensePlateRequired ||
                camelizedRes.facility.supportedFeeTypes.includes(
                    OVERSIZE_FEE_TYPE
                ) ||
                camelizedRes.reservationType ===
                    this.RESERVATION_TYPES.MONTHLY ||
                this.LICENSE_PLATE_REDEMPTION_TYPES.includes(
                    camelizedRes.parkingPassType
                ),
            city: {
                slug: camelizedRes.city.slug,
                title: camelizedRes.city.title,
                countryCode: camelizedRes.city.countryCode,
            },
            facility: {
                title: camelizedRes.facility.title,
                id: camelizedRes.facility.id,
                state: camelizedRes.facility.state,
                barcodeType: camelizedRes.facility.barcodeType,
                oversizeSupported:
                    camelizedRes.facility.oversizeFee > 0 ||
                    camelizedRes.facility.supportedFeeTypes.includes(
                        OVERSIZE_FEE_TYPE
                    ),
                gettingHere: camelizedRes.facility.gettingHere,
                images: camelizedRes.facility.images.map(image => ({
                    urlTemplate: image.urlTemplate,
                    id: image.id,
                    alt: image.alt || null,
                })),
                addresses: camelizedRes.facility.addresses.map(address => {
                    return {
                        city: address.city,
                        state: address.state,
                        streetAddress: address.streetAddress,
                        zipcode: address.zipcode,
                        types: address.types,
                        latitude: address.latitude,
                        longitude: address.longitude,
                    };
                }),
                phoneNumberRequired: camelizedRes.facility.phoneNumberRequired,
                licensePlateRequired:
                    camelizedRes.facility.licensePlateRequired,
                restrictions,
                redemptionInstructions: camelizedRes.redemptionInstructions.map(
                    item => {
                        return {
                            id: item.id,
                            text: item.text,
                        };
                    }
                ),
                amenitiesFull: camelizedRes.amenitiesFull.map(amenity => ({
                    slug: amenity.slug,
                    name: amenity.name,
                })),
                allowUpdatingReservationAfterEntry:
                    camelizedRes.facility.allowUpdatingReservationAfterEntry,
            },
            vehicle:
                camelizedRes.vehicle &&
                camelizedRes.vehicle.licensePlate?.value !==
                    this.DONT_KNOW_LICENSE_VALUE
                    ? {
                          licensePlateValue:
                              camelizedRes.vehicle.licensePlate?.value,
                          makeLabel: camelizedRes.vehicle.info?.displayMake,
                          modelLabel: camelizedRes.vehicle.info?.displayModel,
                          vehicleProfileId: String(camelizedRes.vehicle.id),
                          unlistedModel: camelizedRes.vehicle?.unlistedModel,
                      }
                    : null,
            displayId: camelizedRes.displayId,
            rentalId: camelizedRes.rentalId,
            currencyType: camelizedRes.currencyType,
            reservationType: camelizedRes.reservationType,
            parkingPassType: camelizedRes.parkingPassType,
            displayBarcode: camelizedRes.displayBarcode,
            priceBreakdown: {
                totalPrice: camelizedRes.priceBreakdown.totalPrice,
                totalPaid:
                    camelizedRes.priceBreakdown.totalPrice -
                    camelizedRes.discountAmount -
                    camelizedRes.spotheroCreditUsed,
                totalPossibleRefund:
                    camelizedRes.priceBreakdown.totalPrice -
                    camelizedRes.discountAmount,
                items: camelizedRes.priceBreakdown.items.map(item => {
                    return {
                        price: item.price,
                        shortDescription: item.shortDescription,
                        type: item.type,
                        isPromocodeEligible: item.isPromocodeEligible,
                    };
                }),
            },
            discountAmount: camelizedRes.discountAmount,
            spotheroCreditUsed: camelizedRes.spotheroCreditUsed,
            // For now at least, treat all cancelled like refund to match current parking pass.
            // Follow up with Verticals work to get more granular, particularly for monthly
            paymentStatus:
                camelizedRes.reservationStatus ===
                this.RESERVATION_STATUSES.CANCELLED
                    ? this.PAYMENT_STATUSES.REFUND
                    : camelizedRes.paymentStatus,
            stallName: camelizedRes.stallName,
            canUpdateStartsAndEnds: camelizedRes.canUpdateStartsAndEnds,
            monthlyAgreementLink: camelizedRes.monthlyAgreementLink,
            monthlyRecurring: camelizedRes.monthlyRecurring,
            monthlyInOutDetail: {
                inOutAllowed: camelizedRes.monthlyInOutDetail?.inOutAllowed,
            },
            phoneNumber: camelizedRes.phoneNumber?.replace(/^\+1/, ''),
            event: camelizedRes.event
                ? {
                      title: camelizedRes.event.title,
                      id: camelizedRes.event.id,
                  }
                : null,
            commuterRateDetails: {
                hasRate: camelizedRes.onlineCommuterRate,
                enterStart: camelizedRes.onlineCommuterRateEnterStart,
                enterEnd: camelizedRes.onlineCommuterRateEnterEnd,
            },
            accessHours,
            accessHoursComment: camelizedRes.accessHoursComment,
            rateTitle: camelizedRes.rateTitle,
            renterId: camelizedRes.renterId,
            creditCard: camelizedRes.creditCard
                ? {
                      ...creditCardTransformer(camelizedRes.creditCard),
                      paypalEmail: camelizedRes.creditCard.paypalEmail,
                  }
                : null,
            partner: camelizedRes.partner
                ? {
                      displayName: camelizedRes.partner.displayName,
                      slug: camelizedRes.partner.slug,
                      updateTimesEnabled:
                          camelizedRes.partner.reservationUpdateTimesEnabled,
                      updateVehicleEnabled:
                          camelizedRes.partner.reservationUpdateVehicleEnabled,
                      phoneNumber:
                          camelizedRes.partner.customerServicePhoneNumber,
                      phoneNumberFormatted:
                          camelizedRes.partner
                              .customerServicePhoneNumberFormatted,
                  }
                : null,
            airport: camelizedRes.facility.airport
                ? {
                      arrivalInstructions: camelizedRes.facility.airport.redemptionInstructions.arrival.map(
                          item => ({
                              id: item.id,
                              text: item.text,
                          })
                      ),
                      departureInstructions: camelizedRes.facility.airport.redemptionInstructions.departure.map(
                          item => ({
                              id: item.id,
                              text: item.text,
                          })
                      ),
                      logos: camelizedRes.facility.logos
                          ? camelizedRes.facility.logos.map(logo => ({
                                urlTemplate: logo.urlTemplate,
                                id: logo.id,
                                alt: logo.alt || null,
                            }))
                          : [],
                      shuttle: camelizedRes.facility.airport.shuttle
                          ? {
                                fastFrequency:
                                    camelizedRes.facility.airport.shuttle
                                        .fastFrequency,
                                slowFrequency:
                                    camelizedRes.facility.airport.shuttle
                                        .slowFrequency,
                                contactPhoneNumber:
                                    camelizedRes.facility.airport.shuttle
                                        .contactPhoneNumber,
                                duration:
                                    camelizedRes.facility.airport.shuttle
                                        .duration,
                                type:
                                    camelizedRes.facility.airport.shuttle.type,
                                hours: camelizedRes.facility.airport.shuttle
                                    .hours
                                    ? {
                                          text:
                                              camelizedRes.facility.airport
                                                  .shuttle.hours.text,
                                          periods:
                                              camelizedRes.facility.airport.shuttle.hours.periods?.map(
                                                  period => ({
                                                      // Since you need a full date for dayjs to format it,
                                                      // but the only output is the time, then the date is irrelevant

                                                      endDow: period.endDow,
                                                      endTime: dayjs(
                                                          `1/1/1 ${period.endTime}`
                                                      ).format('h:mma'),
                                                      startDow: period.startDow,
                                                      startTime: dayjs(
                                                          `1/1/1 ${period.startTime}`
                                                      ).format('h:mma'),
                                                  })
                                              ) || [],
                                      }
                                    : null,
                                images:
                                    camelizedRes.facility.airport.shuttle.images
                                        ?.length > 0
                                        ? camelizedRes.facility.airport.shuttle.images.map(
                                              image => ({
                                                  urlTemplate:
                                                      image.urlTemplate,
                                                  id: image.id,
                                                  alt: image.alt || null,
                                              })
                                          )
                                        : [],
                            }
                          : null,
                      details: camelizedRes.facility.airport.details
                          ? {
                                code:
                                    camelizedRes.facility.airport.details.code,
                            }
                          : null,
                  }
                : null,
            ruleReservationType: camelizedRes.ruleReservationType || null,
            addOns: camelizedRes.addOns,
            afterFirstMonth: camelizedRes.afterFirstMonth || [],
        };
    },

    /* strip out html tags and translate nbsp */
    transformRedemptionInstructions(redemptionInstructions) {
        return redemptionInstructions.map(instruction => ({
            ...instruction,
            text: instruction.text
                .replace(/(<([^>]+)>)/gi, '')
                .replace(/&nbsp;/g, ' '),
        }));
    },

    transformRateDiffResponse(response) {
        if (!response) {
            return {};
        }

        const camelizedRes = ObjectUtils.camelizeKeys(response);
        const originalRentalPrice = camelizedRes.originalRental?.price || 0;
        const newPrice = camelizedRes.priceBreakdown?.price || 0;

        return {
            starts: camelizedRes.starts,
            ends: camelizedRes.ends,
            available: camelizedRes.available,
            originalRental: {
                price: originalRentalPrice,
                amountCharged: camelizedRes.originalRental?.amountCharged || 0,
                discountAmount:
                    camelizedRes.originalRental?.discountAmount || 0,
                spotheroCreditUsed:
                    camelizedRes.originalRental?.spotheroCreditUsed || 0,
            },
            priceBreakdown: {
                price: newPrice,
                chargeAmount: {
                    charge:
                        camelizedRes.priceBreakdown?.chargeAmount?.charge || 0,
                    credit:
                        camelizedRes.priceBreakdown?.chargeAmount?.credit || 0,
                },
                refundAmount: {
                    charge:
                        camelizedRes.priceBreakdown?.refundAmount?.charge || 0,
                    credit:
                        camelizedRes.priceBreakdown?.refundAmount?.credit || 0,
                },
            },
            priceDifference: newPrice - originalRentalPrice,
        };
    },

    transformRefundEligibility(eligibility) {
        const camelizedEligibility = ObjectUtils.camelizeKeys(eligibility);

        return {
            originalSourceRefundEligible:
                camelizedEligibility.originalSourceRefund.isEligible,
            creditOnlyRefundEligible:
                camelizedEligibility.creditOnlyRefund.isEligible,
        };
    },
};

export default ReservationUtils;
